import React, { useState } from "react";
import { AlertTriangle, FileDown, Loader2, Trash2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Two-step property deletion:
 *   1. User MUST download the full property archive (PDF)
 *   2. User MUST type the property name exactly
 *   3. Only then is the Delete button enabled
 */
export default function DeletePropertyModal({ property, onCancel, onConfirmDelete }) {
  const [downloaded, setDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);

  const nameOk = typed.trim() === property.name.trim();
  const canDelete = downloaded && nameOk && !deleting;

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("roost_token");
      const res = await fetch(`${API}/properties/${property.id}/statement`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("PDF error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `property-archive-${property.name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      toast.success("Archive downloaded — you may now delete");
    } catch {
      toast.error("Could not generate archive");
    } finally {
      setDownloading(false);
    }
  };

  const confirm = async () => {
    setDeleting(true);
    try {
      await onConfirmDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-property-modal">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-xl fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-50 text-red-700 grid place-items-center">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <h3 className="font-display font-semibold text-lg">Delete property</h3>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-900 transition"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="text-sm text-slate-700">
            You are about to permanently delete <b>{property.name}</b> along with every tenant, lease, payment record, deposit ledger entry and reminder attached to it. <b>This cannot be undone.</b>
          </div>

          {/* Step 1: Archive */}
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Step 1</div>
                <div className="font-display font-semibold">Download full property archive</div>
                <p className="text-sm text-slate-600 mt-1">A PDF with every tenant, lease term, payment and deposit entry for your records. Required before deletion.</p>
              </div>
              {downloaded ? (
                <span className="pill pill-paid" data-testid="archive-downloaded-pill"><CheckCircle2 className="w-3 h-3" /> Saved</span>
              ) : (
                <button
                  onClick={download}
                  disabled={downloading}
                  data-testid="download-archive-btn"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-60"
                >
                  {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                  {downloading ? "Preparing…" : "Download PDF"}
                </button>
              )}
            </div>
          </div>

          {/* Step 2: Type name */}
          <div className={`border rounded-lg p-4 ${downloaded ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Step 2</div>
            <div className="font-display font-semibold">Type the property name to confirm</div>
            <p className="text-sm text-slate-600 mt-1 mb-3">Type <span className="font-mono-plex bg-slate-100 px-1.5 py-0.5 rounded">{property.name}</span> exactly.</p>
            <input
              type="text"
              value={typed}
              disabled={!downloaded}
              onChange={(e) => setTyped(e.target.value)}
              data-testid="delete-confirm-name-input"
              placeholder={property.name}
              className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm disabled:bg-slate-50"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onCancel} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
          <button
            onClick={confirm}
            disabled={!canDelete}
            data-testid="delete-property-confirm-btn"
            className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
}
